import { inject } from "ui-common/di";
import type { IRpcService } from "ui-common/services/rpc/types";
import type {
  CreateCommunityResult,
  UpdateCommunityResult,
  GetCommunitiesResult,
  GetCommunityEventsResult,
} from "api-contracts/rpc/community";
import type { GetLiveEventsResult } from "api-contracts/rpc/get-live-events";
import { saveCollaborators, type ExistingCoPromoter } from "ui-common/services/co-promotion-search/save-collaborators";
import { loadCoPromoters } from "ui-common/services/co-promotion-search/load-co-promoters";
import type {
  IWritableCommunityFormState,
  ICommunityFormService,
  CommunityFormData,
  CommunityUIToggles,
} from "./types";

export class CommunityFormService implements ICommunityFormService {
  @inject<IWritableCommunityFormState>("writableCommunityFormState")
  private state!: IWritableCommunityFormState;

  @inject<IRpcService>("rpcService")
  private rpcService!: IRpcService;

  private pendingLoad: Promise<void> | undefined;
  private pendingLoadId: string | undefined;
  private loadedCoPromoters: ExistingCoPromoter[] = [];

  updateFormData<K extends keyof CommunityFormData>(field: K, value: CommunityFormData[K]): void {
    const current = this.state.formData.get();
    this.state.formData.set({ ...current, [field]: value });

    const errors = this.state.validationErrors.get();
    if (errors[field]) {
      const newErrors = { ...errors };
      delete newErrors[field];
      this.state.validationErrors.set(newErrors);
    }
  }

  validateForm(): boolean {
    const data = this.state.formData.get();
    const errors: Record<string, string> = {};

    if (!data.title.trim()) {
      errors.title = "Title is required";
    }

    this.state.validationErrors.set(errors);
    return Object.keys(errors).length === 0;
  }

  async saveAsDraft(): Promise<boolean> {
    this.state.isSubmitting.set(true);

    try {
      return await this.saveAsDraftInternal();
    } catch {
      return false;
    } finally {
      this.state.isSubmitting.set(false);
    }
  }

  async publish(): Promise<boolean> {
    if (!this.validateForm()) return false;

    this.state.isSubmitting.set(true);

    try {
      const saved = await this.saveAsDraftInternal();
      if (!saved) return false;

      const communityId = this.state.communityId.get();
      if (!communityId) return false;

      const result = await this.rpcService.call<UpdateCommunityResult>("update-community", {
        community_id: communityId,
        status: "published",
      });
      this.state.communityStatus.set(result.community.status);
      return true;
    } catch {
      return false;
    } finally {
      this.state.isSubmitting.set(false);
    }
  }

  async loadCommunity(id: string): Promise<void> {
    if (this.pendingLoadId === id && this.pendingLoad) {
      return this.pendingLoad;
    }
    this.pendingLoadId = id;
    this.pendingLoad = this.doLoadCommunity(id);
    try {
      await this.pendingLoad;
    } finally {
      this.pendingLoad = undefined;
      this.pendingLoadId = undefined;
    }
  }

  async loadAvailableEvents(): Promise<void> {
    try {
      const result = await this.rpcService.call<GetLiveEventsResult>("get-live-events", {});
      const options = result.events.map((e) => ({ value: e.id, label: e.title }));
      this.state.availableEvents.set(options);
    } catch (error) {
      console.error("[CommunityForm] Failed to load available events:", error);
      this.state.availableEvents.set([]);
    }
  }

  setThumbnailData(fileId: string | undefined, url: string | undefined): void {
    this.state.thumbnailFileId.set(fileId);
    this.state.thumbnailUrl.set(url);
  }

  setCollaboratorHandles(handles: string[]): void {
    this.state.collaboratorHandles.set(handles);
  }

  toggleLinkedEvent(eventId: string, checked: boolean): void {
    const current = this.state.linkedEventIds.get();
    if (checked) {
      if (!current.includes(eventId)) {
        this.state.linkedEventIds.set([...current, eventId]);
      }
    } else {
      this.state.linkedEventIds.set(current.filter((id) => id !== eventId));
    }
  }

  updateUIToggle<K extends keyof CommunityUIToggles>(toggle: K, value: CommunityUIToggles[K]): void {
    const current = this.state.uiToggles.get();
    this.state.uiToggles.set({ ...current, [toggle]: value });
  }

  reset(): void {
    this.state.reset();
    this.loadedCoPromoters = [];
  }

  private async saveAsDraftInternal(): Promise<boolean> {
    const data = this.state.formData.get();
    const communityId = this.state.communityId.get();

    if (communityId) {
      const thumbnailFileId = this.state.thumbnailFileId.get();
      const result = await this.rpcService.call<UpdateCommunityResult>("update-community", {
        community_id: communityId,
        title: data.title,
        description: data.description || undefined,
        ...(thumbnailFileId !== undefined ? { thumbnail_file_id: thumbnailFileId } : {}),
        cta_text: data.ctaText || null,
        language: data.language || null,
        live_event_ids: this.state.linkedEventIds.get(),
        show_member_count: data.showMemberCount,
      });
      this.state.communityStatus.set(result.community.status);
    } else {
      const result = await this.rpcService.call<CreateCommunityResult>("create-community", {
        title: data.title,
        description: data.description || undefined,
        thumbnail_file_id: this.state.thumbnailFileId.get(),
        cta_text: data.ctaText || undefined,
        language: data.language || undefined,
        live_event_ids: this.state.linkedEventIds.get(),
        show_member_count: data.showMemberCount,
      });
      this.state.communityId.set(result.community.id);
      this.state.communityStatus.set(result.community.status);
    }

    await this.saveCoPromotions();
    return true;
  }

  private async saveCoPromotions(): Promise<void> {
    const communityId = this.state.communityId.get();
    const handles = this.state.collaboratorHandles.get();
    if (!communityId) return;

    await saveCollaborators(this.rpcService, "community", communityId, handles, this.loadedCoPromoters);
  }

  private async doLoadCommunity(id: string): Promise<void> {
    this.state.isLoading.set(true);
    try {
      const result = await this.rpcService.call<GetCommunitiesResult>("get-communities", {});
      const community = result.communities.find((c) => c.id === id);
      if (!community) return;

      this.state.formData.set({
        title: community.title,
        description: community.description ?? "",
        language: community.language ?? "",
        ctaText: community.cta_text ?? "",
        showMemberCount: community.show_member_count !== false,
      });
      this.state.communityId.set(community.id);
      this.state.communityStatus.set(community.status);
      this.state.thumbnailUrl.set(community.thumbnail_url ?? undefined);

      const eventsResult = await this.rpcService.call<GetCommunityEventsResult>("get-community-events", {
        community_id: id,
      });
      const eventIds = eventsResult.events.map((e) => e.id);
      this.state.linkedEventIds.set(eventIds);

      if (eventIds.length > 0) {
        this.state.uiToggles.set({ ...this.state.uiToggles.get(), showLinkedEvents: true });
      }
      if (community.cta_text) {
        this.state.uiToggles.set({ ...this.state.uiToggles.get(), showCustomCta: true });
      }

      const { loadedCoPromoters: loaded } = await loadCoPromoters(this.rpcService, "community", id, this.state);
      this.loadedCoPromoters = loaded;
    } finally {
      this.state.isLoading.set(false);
    }
  }
}
